import { Immutable } from "immer";
import React from "react";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type { FeaturesModule as Value } from "@reactivated";

type Props = Immutable<{
    value: Value;
}>;

export const FeaturesModule = (props: Props): React.JSX.Element => {
    const v = props.value;
    const featuresImageList = v.features_image_list ?? [];
    const features = v.features ?? [];
    const hasImageList = featuresImageList.length > 0;
    const hasFeatures = features.length > 0;
    // watchCSS gates the carousel to the mobile breakpoint.
    const imageContainerRef = React.useRef<HTMLDivElement>(null);
    useFlickity(imageContainerRef, {
        module: "flickity",
        options: { watchCSS: true, autoPlay: true },
        enabled: hasImageList,
        deps: [hasImageList],
    });
    return (
        <div className="pdp-features__container l-full-width">
            {/*
                Note: Changing the ID below will break the tooltip
                functionality in the configurator upsell. Change in
                'component/configurator.tsx' as well, if needed.
            */}
            <div id="pdp-features" className="pdp-section pdp-features">
                <div className="pdp-features__header l-capped-width">
                    <h2
                        className="pdp-features__title pdp-section__title"
                        dangerouslySetInnerHTML={{
                            __html: v.features_title ?? "",
                        }}
                    />
                    <div
                        className="pdp-features__desc pdp-section__desc"
                        dangerouslySetInnerHTML={{
                            __html: v.features_description ?? "",
                        }}
                    />
                </div>
                <div className="pdp-section_body pdp-features__body">
                    <div className="pdp-features__content">
                        {hasImageList && (
                            <>
                                {hasFeatures && (
                                    <div className="pdp-features__value-props-container l-capped-width">
                                        {features.map((feature, i) => (
                                            <div
                                                key={i}
                                                className="pdp-features__value-props"
                                            >
                                                <div className="pdp-features__value-props__image">
                                                    <img
                                                        className="pdp-features__value-props__image-item"
                                                        src={
                                                            feature.image
                                                                ?.url ?? ""
                                                        }
                                                        alt=""
                                                        loading="lazy"
                                                        decoding="async"
                                                    />
                                                </div>
                                                <div className="pdp-features__value-props__title">
                                                    {feature.title ?? ""}
                                                </div>
                                                <div className="pdp-features__value-props__desc pdp-section__desc">
                                                    {feature.desc ?? ""}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div
                                    className="pdp-features__image-container"
                                    ref={imageContainerRef}
                                >
                                    {featuresImageList.map(
                                        (featuresImage, i) => (
                                            <div
                                                key={i}
                                                className={`pdp-features__image ${
                                                    i ===
                                                    featuresImageList.length - 1
                                                        ? " pdp-features__image--last "
                                                        : ""
                                                }`}
                                            >
                                                <img
                                                    className="pdp-features__image-item responsive-img"
                                                    alt={
                                                        featuresImage.desc ?? ""
                                                    }
                                                    aria-hidden="true"
                                                    src={
                                                        featuresImage.image
                                                            ?.url ?? ""
                                                    }
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                                <p className="pdp-features__image-desc">
                                                    {featuresImage.desc ?? ""}
                                                </p>
                                            </div>
                                        ),
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
